import React from 'react';
import { Clock, Zap, Crown, Gift } from 'lucide-react';
import { useProgress } from '../../../context/ProgressContext';
import { useAnimatedNumber } from '../../../hooks/useAnimatedNumber';

const CountdownSection = () => {
  const { joinedMembers, spotsLeft, progressPercentage } = useProgress();
  const animatedJoined = useAnimatedNumber(joinedMembers, 1500);
  const animatedSpotsLeft = useAnimatedNumber(spotsLeft, 1500);

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 border border-gray-300 rounded-full"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 border border-gray-300 rounded-full"></div>
      </div>
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 rounded-full px-6 py-2 text-sm font-semibold mb-6">
            <Zap className="w-4 h-4" />
            Limited Time Opportunity
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            First <span className="text-orange-500">1,000</span> Founding Members
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Secure your exclusive position in ProConnect's founding community. 
            Once we reach 1,000 members, this opportunity closes forever.
          </p>
        </div>

        {/* Progress Visualization */}
        <div className="bg-gray-50 rounded-3xl p-8 md:p-12 mb-16">
          <div className="text-center mb-8">
            <div className="text-6xl md:text-8xl font-black text-primary mb-4 transition-all duration-1000">
              {animatedSpotsLeft?.toLocaleString()}
            </div>
            <div className="text-2xl font-semibold text-gray-700">Spots Remaining</div>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div 
              className="w-full rounded-full h-6 mb-4"
              style={{ backgroundColor: 'rgba(30, 64, 175, 0.1)' }}
            >
              <div 
                className="h-6 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                style={{ 
                  width: `${progressPercentage}%`,
                  backgroundColor: '#F97316' 
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
              </div>
            </div>
            
            <div className="flex justify-between text-sm font-medium text-gray-600">
              <span>0 Members</span>
              <span 
                className="font-bold transition-all duration-1000"
                style={{ color: '#F97316' }}
              >{animatedJoined?.toLocaleString()} Joined</span>
              <span>1,000 Limit</span>
            </div>
          </div>
        </div>

        {/* Urgency Features Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center group">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Time-Limited Access</h3>
            <p className="text-gray-600 leading-relaxed">
              Registration closes permanently once we reach 1,000 founding members. No exceptions, no extensions.
            </p>
          </div>

          <div className="text-center group">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Exclusive Status</h3>
            <p className="text-gray-600 leading-relaxed">
              Founding member badge and privileges that will never be available to regular users joining later.
            </p>
          </div>

          <div className="text-center group">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Lifetime Benefits</h3>
            <p className="text-gray-600 leading-relaxed">
              $50 credit, priority service, and free app access - benefits that continue for the lifetime of your membership.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountdownSection;